import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Zap, Wifi, Bell } from 'lucide-react';

const DownloadAppBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Check if should show banner
    const shouldShowBanner = () => {
      // Don't show if already a PWA
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSPWA = window.navigator.standalone === true;
      if (isStandalone || isIOSPWA) return false;

      // Don't show on desktop
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (!isMobile) return false;

      // Check if dismissed recently (7 days)
      const dismissedAt = localStorage.getItem('pwa_banner_dismissed');
      if (dismissedAt) {
        const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) return false;
      }

      // Check visit count (show after 2nd visit)
      const visitCount = parseInt(localStorage.getItem('visit_count') || '0') + 1;
      localStorage.setItem('visit_count', visitCount.toString());
      if (visitCount < 2) return false;

      return true;
    };

    // Delay showing banner for better UX
    const timer = setTimeout(() => {
      if (shouldShowBanner()) {
        setShowBanner(true);
      }
    }, 3000);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      setIsInstalling(true);
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowBanner(false);
        localStorage.setItem('pwa_installed', 'true');
      }
      setDeferredPrompt(null);
      setIsInstalling(false);
    } else {
      // iOS doesn't support beforeinstallprompt, show instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        alert('To install: tap the Share button below, then "Add to Home Screen"');
      }
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_banner_dismissed', Date.now().toString());
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-20 left-3 right-3 z-[60] safe-bottom"
          role="banner"
          aria-label="Install app prompt"
        >
          <div className="bg-gradient-to-r from-[#0A0A0F] to-[#12121A] border border-white/10 rounded-2xl p-4 shadow-2xl shadow-black/50">
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 p-1 text-gray-500 hover:text-white transition-colors touch-target"
              aria-label="Dismiss banner"
              data-testid="dismiss-install-banner"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-start gap-4">
              {/* App Icon */}
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FF2A2A] via-[#FF6B6B] to-[#FFE800] flex items-center justify-center shadow-lg">
                  <Smartphone className="w-7 h-7 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pr-6">
                <h3 className="font-bold text-white text-base mb-1">
                  Get the Pulsate App
                </h3>
                <p className="text-sm text-gray-400 mb-3">
                  Install for faster access & offline support
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/5 rounded-full text-xs text-gray-300">
                    <Zap className="w-3 h-3 text-[#FFE800]" />
                    Faster
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/5 rounded-full text-xs text-gray-300">
                    <Wifi className="w-3 h-3 text-[#00F0FF]" />
                    Offline
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/5 rounded-full text-xs text-gray-300">
                    <Bell className="w-3 h-3 text-[#FF2A2A]" />
                    Notifications
                  </span>
                </div>

                {/* Install Button */}
                <button
                  onClick={handleInstall}
                  disabled={isInstalling}
                  className="w-full py-2.5 bg-gradient-to-r from-[#00F0FF] to-[#00C8FF] text-black font-semibold rounded-xl flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(0,240,255,0.3)] transition-all disabled:opacity-50 touch-target"
                  data-testid="install-app-btn"
                >
                  <Download className="w-4 h-4" />
                  {isInstalling ? 'Installing...' : 'Install Free'}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DownloadAppBanner;
